export const specialCharactersRegex = /\W/g
export const stripSpecialCharacters = s => s.replace(specialCharactersRegex, '')

/**
 * isIdEqual strips special characters from ids to compare them.
 * E.g. AB-CDE123 to ABCDE123
 *
 * When coming back from wallet (adding a card or linking a bank account), it
 * passes the new FI ID (in base64) via a query string called "success". This FI
 * ID contains a dash (special character) while the funding options returned
 * from services do NOT contain the dash. To avoid issues, we simply remove
 * special characters from the IDs before comparing them.
 *
 * @param {String} a funding option id 1
 * @param {String} b funding option id 2
 */
export function isIdEqual(a, b) {
  return stripSpecialCharacters(a) === stripSpecialCharacters(b)
}

/**
 * updateFundingOption updates a funding option given it's ID
 * @param {Array<String>} sourceIds
 * @param {Array<fundingOption>} fundingOptions
 * @param {String} fundingOptionID
 * @param {Array<fundingOption>} callback
 */
export function updateFundingOption(fundingOptions, fundingOptionID, callback) {
  return fundingOptions.map(option => {
    if (fundingOptionID === option.id) {
      return callback(option)
    }
    return option
  })
}

/**
 * includesId returns true if the id is found in the array
 * it uses isIdEqual to make sure the ids are compared without special characters.
 * @param {Array<String>} sourceIds
 * @param {String} id
 */
export function includesId(sourceIds, id) {
  return sourceIds.some(
    sourceId => !!sourceId && !!id && isIdEqual(sourceId, id)
  )
}

/**
 * Returns the exact or partial Funding Option match that contains the given Funding Sources IDs
 * @param {Array<fundingOption>} fundingOptions
 * @param {Array<string>} targetSourceIds
 * @param {Object} options
 * @return {Object|undefined}
 */
export function getOptionBySourceIds(
  fundingOptions,
  targetSourceIds = [],
  { partialMatch = false } = {}
) {
  if (!targetSourceIds.length) {
    return
  }
  const isIdIncluded = ({ id }) => includesId(targetSourceIds, id)
  return fundingOptions.find(({ funding_sources = [] } = {}) =>
    partialMatch
      ? funding_sources.some(isIdIncluded)
      : funding_sources.length === targetSourceIds.length &&
        funding_sources.every(isIdIncluded)
  )
}

/**
 * Grab the option from a list which contains a source id from targetSourceIds (removing the holding instrument type)
 * This is useful to remember the FI selected in the same transaction for split funding use case
 * For example, if a user selected (visa card+balance) and after replanning we get only visa card without balance,
 * we should still remember the user choice of visa card
 * @param {Array<fundingOption>} fundingOptions
 * @param {Array<string>} targetSourceIds
 * @return {Object|undefined}
 */
export function getOptionBySourceIdsPartialMatch(
  fundingOptions,
  targetSourceIds = []
) {
  return getOptionBySourceIds(fundingOptions, targetSourceIds, {
    partialMatch: true,
  })
}

/**
 * Returns the Funding Instrument (FI) if it matches the provided Id
 * Or the FI with some of its Funding Sources matching the Id
 * @param {Array<fundingOption>} fundingOptions
 * @param {string} Id
 * @returns {Object}
 */
export function getOptionById(fundingOptions = [], Id = '') {
  return (
    // Check for the ID first in the funding options and then we check the
    // funding sources as well. The reason is that when we change the selected FI
    // we get the ID from the funding option, but when we add a new FI, wallet
    // returns the funding source ID, thus when coming back from wallet we need
    // to make sure we select the correct FI.
    fundingOptions.find(({ id } = {}) => includesId([Id], id)) ||
    getOptionBySourceIdsPartialMatch(fundingOptions, Id.split(',')) ||
    {}
  )
}

/**
 * Will return if there is a valid funding option to be used in a transaction from a fundingSourceId
 * @param {Array<fundingOption>} fundingOptions
 * @param {string} fundingSourceId
 * @return {boolean}
 */
export function isFundingOptionAvailable(fundingOptions, fundingSourceId) {
  return !!Object.entries(getOptionById(fundingOptions, fundingSourceId)).length
}

/**
 * Access the holding/bank_account/payment_card/credit object inside funding source
 * @param {Object} source
 * @returns the funding source detail in source.<instrument_type>
 */
export function flattenFundingSource(source) {
  return (
    source.holding ||
    source.bank_account ||
    source.payment_card ||
    source.payment_token ||
    source.credit ||
    {}
  )
}

export function generateDisallowedSources(disallowedSources = []) {
  return disallowedSources.map(source => {
    const flatFundingSource = flattenFundingSource(source)
    return {
      id: flatFundingSource.id,
      instrument_type: source.instrument_type,
      type: flatFundingSource.type,
      network: flatFundingSource.network,
      provider: flatFundingSource.provider,
      reason: source.reason,
      bank_name: flatFundingSource.issuer && flatFundingSource.issuer.name,
    }
  })
}

export function convertBankToDisallowedSource(
  source = {},
  reason = 'PAYMENT_TYPE_NOT_SUPPORTED'
) {
  return {
    ...source,
    instrument_type: source.instrument_type,
    reason: reason,
    bank_account: {
      id: source.id,
      type: source.type,
      issuer: { name: source.bank_name },
    },
  }
}
